export const GOOGLE_TRANSLATE_SCRIPT_ID = "umbrella-google-translate";
export const GOOGLE_TRANSLATE_SCRIPT_URL =
	"https://translate.google.com/translate_a/element.js?cb=umbrellaGoogleTranslateReady";

type TranslateElement = Window["google"]["translate"]["TranslateElement"];
let pending: Promise<TranslateElement> | undefined;
let widget: Promise<HTMLSelectElement> | undefined;
export const GOOGLE_TRANSLATE_WIDGET_ID = "umbrella-google-translate-widget";
export const GOOGLE_TRANSLATE_CHANGE_EVENT = "umbrella-google-translate-change";

export function selectedGoogleTranslateLanguage() {
	return document.querySelector<HTMLSelectElement>(
		`#${GOOGLE_TRANSLATE_WIDGET_ID} .goog-te-combo`,
	)?.value;
}

/** Google's provider supports one widget per document, even with many menus. */
export function getGoogleTranslateSelect(
	pageLanguage: string,
): Promise<HTMLSelectElement> {
	if (widget) return widget;
	widget = loadGoogleTranslate()
		.then(async (TranslateElement) => {
			let container = document.getElementById(GOOGLE_TRANSLATE_WIDGET_ID);
			if (!container) {
				container = document.createElement("div");
				container.id = GOOGLE_TRANSLATE_WIDGET_ID;
				container.className = "notranslate";
				container.hidden = true;
				document.body.appendChild(container);
				new TranslateElement(
					{ pageLanguage, layout: TranslateElement.InlineLayout.VERTICAL },
					container,
				);
			}
			const controller = new AbortController();
			const abort = () => controller.abort();
			window.addEventListener("pagehide", abort, { once: true });
			try {
				const select = await waitForGoogleSelect(container, controller.signal);
				select.addEventListener("change", () =>
					document.dispatchEvent(new Event(GOOGLE_TRANSLATE_CHANGE_EVENT)),
				);
				return select;
			} finally {
				window.removeEventListener("pagehide", abort);
			}
		})
		.catch((error) => {
			widget = undefined;
			throw error;
		});
	return widget;
}

function available() {
	const api = window.google?.translate?.TranslateElement;
	return typeof api === "function" && api.InlineLayout ? api : undefined;
}

/** One on-demand provider per document, shared by all mounted selectors. */
export function loadGoogleTranslate(): Promise<TranslateElement> {
	const api = available();
	if (api) return Promise.resolve(api);
	if (pending) return pending;
	pending = new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
		script.src = GOOGLE_TRANSLATE_SCRIPT_URL;
		script.async = true;
		const previous = window.umbrellaGoogleTranslateReady;
		const cleanup = () => {
			window.clearTimeout(timeout);
			script.onerror = null;
			if (previous) window.umbrellaGoogleTranslateReady = previous;
			else delete window.umbrellaGoogleTranslateReady;
		};
		const fail = () => {
			cleanup();
			script.remove();
			pending = undefined;
			reject(new Error("Google Translate could not be loaded."));
		};
		window.umbrellaGoogleTranslateReady = () => {
			const loaded = available();
			if (!loaded) return fail();
			cleanup();
			resolve(loaded);
		};
		const timeout = window.setTimeout(fail, 10_000);
		script.onerror = fail;
		document.head.appendChild(script);
	});
	return pending;
}

/** Widget markup can arrive after the provider callback; observe only its host. */
export function waitForGoogleSelect(
	container: HTMLElement,
	signal: AbortSignal,
): Promise<HTMLSelectElement> {
	return new Promise((resolve, reject) => {
		if (signal.aborted) {
			reject(new DOMException("Translation widget unmounted.", "AbortError"));
			return;
		}
		const cleanup = () => {
			observer.disconnect();
			window.clearTimeout(timeout);
			signal.removeEventListener("abort", abort);
		};
		const check = () => {
			const select =
				container.querySelector<HTMLSelectElement>(".goog-te-combo");
			if (select) {
				cleanup();
				resolve(select);
			}
		};
		const abort = () => {
			cleanup();
			reject(new DOMException("Translation widget unmounted.", "AbortError"));
		};
		const observer = new MutationObserver(check);
		const timeout = window.setTimeout(() => {
			cleanup();
			reject(new Error("Google Translate did not initialize."));
		}, 10_000);
		signal.addEventListener("abort", abort, { once: true });
		observer.observe(container, { childList: true, subtree: true });
		check();
	});
}
